import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Lock, Eye, Database, Trash, UserCircle } from '@phosphor-icons/react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-[#5B21B6] font-['Outfit']" data-testid="nav-logo">
            MenoPath
          </Link>
          <Link to="/">
            <button className="flex items-center gap-2 text-[#5B21B6] hover:text-[#4C1D95] transition-colors font-medium">
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-white" weight="duotone" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#1E1B4B] font-['Outfit']">
                  Privacy Policy
                </h1>
                <p className="text-[#6B7280]">Last Updated: January 25, 2026</p>
              </div>
            </div>

            {/* Healthcare Privacy Commitment Banner */}
            <div className="bg-gradient-to-r from-[#D1FAE5] to-[#A7F3D0] rounded-2xl p-6 mb-8">
              <h2 className="text-xl font-bold text-[#065F46] font-['Outfit'] mb-2">
                Your Health Data is Sacred to Us
              </h2>
              <p className="text-[#047857]">
                At Menopath.ai, we understand the sensitive nature of health information. 
                We are committed to protecting your privacy and will <strong>never sell, share, 
                or monetize your personal health data</strong> with third parties for advertising 
                or marketing purposes.
              </p>
            </div>

            <div className="prose prose-lg max-w-none text-[#374151] space-y-6">
              
              <section>
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3 flex items-center gap-2">
                  <Database className="w-6 h-6 text-[#5B21B6]" />
                  Information We Collect
                </h2>
                <p>We collect information you provide directly to us, including:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li><strong>Account Information:</strong> Name, email address, and password when you create an account</li>
                  <li><strong>Health Data:</strong> Hormonal test results (FSH, LH, Estradiol, AMH, Inhibin B, Progesterone) and physical measurements (age, height, weight)</li>
                  <li><strong>Lifestyle Information:</strong> Sleep quality, exercise frequency, smoking status, and alcohol consumption</li>
                  <li><strong>Usage Data:</strong> How you interact with our platform to improve our services</li>
                </ul>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3 flex items-center gap-2">
                  <Eye className="w-6 h-6 text-[#5B21B6]" />
                  How We Use Your Information
                </h2>
                <p>We use your information solely for the following purposes:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>To provide personalized menopause predictions and health insights</li>
                  <li>To generate your My MenoPath Index (MMI) chart and analysis</li>
                  <li>To improve and develop our AI prediction algorithms</li>
                  <li>To communicate with you about your account and our services</li>
                  <li>To ensure the security and integrity of our platform</li>
                </ul>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3 flex items-center gap-2">
                  <Lock className="w-6 h-6 text-[#5B21B6]" />
                  Data Protection & Security
                </h2>
                <p>
                  We implement industry-standard security measures to protect your personal 
                  and health information:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>All data is encrypted in transit using TLS/SSL protocols</li>
                  <li>Health data is stored in encrypted databases with restricted access</li>
                  <li>We use secure authentication methods including password hashing</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Employee access to personal data is limited and logged</li>
                </ul>
              </section>

              {/* Key Healthcare Privacy Commitment */}
              <section className="mt-8 p-6 bg-[#EDE9FE] rounded-2xl border-2 border-[#5B21B6]">
                <h2 className="text-xl font-bold text-[#5B21B6] font-['Outfit'] mb-3">
                  Our Healthcare Data Promise
                </h2>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#D1FAE5] flex items-center justify-center flex-shrink-0 mt-1">
                      <ShieldCheck className="w-4 h-4 text-[#10B981]" weight="bold" />
                    </div>
                    <p className="text-[#1E1B4B]">
                      <strong>We will NEVER sell your health data</strong> to advertisers, data brokers, 
                      or any third parties.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#D1FAE5] flex items-center justify-center flex-shrink-0 mt-1">
                      <ShieldCheck className="w-4 h-4 text-[#10B981]" weight="bold" />
                    </div>
                    <p className="text-[#1E1B4B]">
                      <strong>We will NEVER share your personal information</strong> for marketing 
                      or promotional purposes without your explicit consent.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#D1FAE5] flex items-center justify-center flex-shrink-0 mt-1">
                      <ShieldCheck className="w-4 h-4 text-[#10B981]" weight="bold" />
                    </div>
                    <p className="text-[#1E1B4B]">
                      <strong>Your health data remains yours.</strong> You can request deletion 
                      of your data at any time.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Information Sharing
                </h2>
                <p>
                  We only share your information in the following limited circumstances:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
                  <li><strong>Service Providers:</strong> With trusted vendors who assist in operating our platform (under strict confidentiality agreements)</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental regulation</li>
                  <li><strong>Safety:</strong> To protect the rights, safety, and property of Menopath, our users, or the public</li>
                </ul>
                <p className="mt-4 p-4 bg-[#FEF3C7] rounded-xl text-[#92400E]">
                  <strong>Note:</strong> We do NOT share your data with insurance companies, employers, 
                  or any entity that could use your health information against your interests.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3 flex items-center gap-2">
                  <UserCircle className="w-6 h-6 text-[#5B21B6]" />
                  Your Rights
                </h2>
                <p>You have the following rights regarding your personal information:</p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                  <li><strong>Portability:</strong> Receive your data in a portable format</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                </ul>
                <p className="mt-3">
                  To exercise any of these rights, please contact us at{' '}
                  <a href="mailto:privacy@menopath.ai" className="text-[#5B21B6] underline hover:text-[#4C1D95]">
                    privacy@menopath.ai
                  </a>
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3 flex items-center gap-2">
                  <Trash className="w-6 h-6 text-[#5B21B6]" />
                  Data Retention
                </h2>
                <p>
                  We retain your personal information for as long as your account is active or as 
                  needed to provide you with our services. If you delete your account, we will 
                  delete or anonymize your personal data within 30 days, except where retention 
                  is required by law.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Cookies and Tracking
                </h2>
                <p>
                  We use essential cookies to maintain your session and remember your preferences. 
                  We do not use third-party advertising cookies or trackers that monitor your 
                  activity across other websites.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Children's Privacy
                </h2>
                <p>
                  Menopath.ai is not intended for individuals under 18 years of age. We do not 
                  knowingly collect personal information from minors. If we become aware that 
                  we have collected data from a minor, we will delete it immediately.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Changes to This Policy
                </h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of 
                  any material changes by posting the new policy on this page and updating 
                  the "Last Updated" date. Your continued use of the Services after changes 
                  are posted constitutes acceptance of the revised policy.
                </p>
              </section>

              <section className="mt-8">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-3">
                  Contact Us
                </h2>
                <p>
                  If you have questions or concerns about this Privacy Policy or our data 
                  practices, please contact us:
                </p>
                <div className="mt-4 p-4 bg-[#F3F4F6] rounded-xl">
                  <p><strong>Email:</strong>{' '}
                    <a href="mailto:privacy@menopath.ai" className="text-[#5B21B6] underline hover:text-[#4C1D95]">
                      privacy@menopath.ai
                    </a>
                  </p>
                  <p className="mt-2"><strong>General Support:</strong>{' '}
                    <a href="mailto:support@menopath.ai" className="text-[#5B21B6] underline hover:text-[#4C1D95]">
                      support@menopath.ai
                    </a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-[#1E1B4B] py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm">© 2026 MenoPath. All rights reserved.</p>
          <div className="mt-4 flex justify-center gap-6">
            <Link to="/terms" className="text-white/70 hover:text-white transition-colors text-sm">Terms of Service</Link>
            <Link to="/privacy" className="text-white/70 hover:text-white transition-colors text-sm">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
